// Actor: walks a path at fixed speed. Pure simulation state (no render deps),
// so it is deterministic and replayable.
import { dist, type Vec2 } from "../core/geometry";

export interface ActorOpts {
  radius?: number;
  /** immovable actors (idle NPCs) act as obstacles the hero slides around */
  movable?: boolean;
  speed?: number;
}

export class Actor {
  pos: Vec2;
  facing: 1 | -1 = 1;
  speed: number;
  radius: number;
  movable: boolean;
  private path: Vec2[] = [];

  constructor(spawn: Vec2, opts: ActorOpts = {}) {
    this.pos = { x: spawn.x, y: spawn.y };
    this.radius = opts.radius ?? 8;
    this.movable = opts.movable ?? true;
    this.speed = opts.speed ?? 90;
  }

  get moving(): boolean {
    return this.path.length > 0;
  }
  get feetY(): number {
    return this.pos.y;
  }
  get remaining(): Vec2[] {
    return this.path;
  }

  setPath(path: Vec2[]): void {
    this.path = path.map((p) => ({ x: p.x, y: p.y }));
    while (this.path.length && dist(this.path[0]!, this.pos) < 0.5) this.path.shift();
  }

  stop(): void {
    this.path = [];
  }

  update(dt: number): void {
    let budget = this.speed * dt;
    while (budget > 0 && this.path.length) {
      const tgt = this.path[0]!;
      const d = dist(this.pos, tgt);
      if (tgt.x !== this.pos.x) this.facing = tgt.x < this.pos.x ? -1 : 1;
      if (d <= budget) {
        this.pos = { x: tgt.x, y: tgt.y };
        this.path.shift();
        budget -= d;
      } else {
        const f = budget / d;
        this.pos = { x: this.pos.x + (tgt.x - this.pos.x) * f, y: this.pos.y + (tgt.y - this.pos.y) * f };
        budget = 0;
      }
    }
  }
}
